import {
  AppointmentControllerService,
  AppointmentParticipantResponse,
  AppointmentResponse,
  AppointmentSchedulingControllerService,
  AvailableSlotResponse
} from "@9amhealth/openapi";
import {
  CalendarDate,
  CalendarDateTime,
  DateValue,
  endOfMonth,
  fromDate,
  getLocalTimeZone,
  startOfMonth,
  toCalendarDate,
  toCalendarDateTime,
  today,
  ZonedDateTime
} from "@internationalized/date";
import { Cubit } from "blac-next";

export type AppointmentSlot = {
  from: ZonedDateTime;
  to: ZonedDateTime;
  componentIdentifier: string;
  participants: Array<AppointmentParticipantResponse>;
};

export type AppointmentSlotGroup = {
  from: ZonedDateTime;
  to: ZonedDateTime;
  slots: AppointmentSlot[];
};

export type SchedulerReconnectStatus =
  | "accepted"
  | "rejected"
  | "dismissed"
  | "available"
  | "unavailable"
  | "unknown";

export type SchedulerState = {
  selectedSlot?: AppointmentSlot;
  selectedSlotGroup?: AppointmentSlotGroup;
  selectedDate?: DateValue;
  availableSlotGroups: AppointmentSlotGroup[];
  reconnectStatus: SchedulerReconnectStatus;
  /**
   * @description
   * null means we dont have a previous provider
   * undefined means we have not checked yet
   */
  previousProvider?: AppointmentParticipantResponse | null;
  loading: boolean;
  previousProviderLoading: boolean;
};

export type SchedulerStep =
  | "confirm"
  | "pick-date"
  | "pick-slot"
  | "pick-provider";

export type SchedulerAppointmentEventData = {
  id: string;
  dateTime: CalendarDateTime;
  duration: number;
};

export type SchedulerProps = {
  availableSlots: AppointmentSlot[];
  appointmentType?: ScheduleAppointmentTypes;
  /** Member selects a date from the calendar */
  onSelectDate: (date: CalendarDate) => void;
  /** Member selects a slot from the list of slots */
  onSelectSlot: (slot: {
    autoSelected?: boolean;
    dateTime: CalendarDateTime;
    duration: number;
    slotProviderId: string;
    slotProviderName: string;
  }) => void;
  /** Member selects a slot group from the list of slot groups */
  onSelectSlotGroup: (slotGroup: {
    dateTime: CalendarDateTime;
    duration: number;
    slotProviderIds: string[];
    slotProviderNames: string[];
  }) => void;
  /** Member deselects a slot */
  onDeselectSlot: () => void;
  /** Member selects a provider from the list of providers */
  onSelectProvider: (provider: { id: string; name: string }) => void;
  /** Member books an appointment */
  onAppointmentBooked: (appointment: SchedulerAppointmentEventData) => void;
  /** Member reschedules an appointment */
  onAppointmentRescheduled: (
    oldAppointment: SchedulerAppointmentEventData,
    newAppointment: SchedulerAppointmentEventData
  ) => void;
  /** Member chooses to reconnect to the previous provider */
  onAcceptReconnectProvider: (
    reconnectStatus: SchedulerReconnectStatus
  ) => void;
  /** Member chooses to not reconnect to the previous provider */
  onRejectReconnectProvider: (
    reconnectStatus: SchedulerReconnectStatus
  ) => void;
  /** Member has the previous provider selected and then dismisses the the provider to see all provider availabilities */
  onPreviousProviderDismissed: (
    reconnectStatus: SchedulerReconnectStatus
  ) => void;
};

export type ScheduleAppointmentTypes = Parameters<
  typeof AppointmentSchedulingControllerService.getAvailableSlots
>[0];

/**
 * SchedulerBloc manages the state and logic for appointment scheduling, including slot selection, provider selection, and booking/rescheduling appointments.
 * It interacts with the backend API to fetch available slots and previous provider information, and exposes methods for UI components to trigger scheduling actions.
 *
 * @remarks
 * - Uses blac-next Cubit for state management.
 * - Handles callbacks for UI events via SchedulerProps.
 */
export class SchedulerBloc extends Cubit<SchedulerState, SchedulerProps> {
  //#region Fields
  // Public fields
  availableSlots: AppointmentSlot[] = [];
  daysPerRequest = 5;
  currentAppointmentType?: ScheduleAppointmentTypes;
  rescheduleAppointment?: AppointmentResponse;
  requestProviders?: string[];
  currentMonth = today(getLocalTimeZone()).month;
  focusedDate?: DateValue;
  debouncedHandleDateFocusChanged: ReturnType<typeof setTimeout> | number = 0;
  loadCompleteForMonths = new Set<number>();
  currentCacheKey = "";
  // Private fields
  private callbacks: Partial<SchedulerProps> = {};
  private debugCallbacks = false;
  private enableSchedulerLogs = false;
  //#endregion

  //#region Constructor
  /**
   * Initializes the SchedulerBloc with optional props and fetches previous provider if appointmentType is provided.
   * @param props - Optional SchedulerProps for callbacks and configuration.
   */
  constructor(props?: SchedulerProps) {
    super({
      loading: false,
      availableSlotGroups: [],
      previousProvider: undefined,
      previousProviderLoading: false,
      reconnectStatus: "unknown"
    });
    if (props) {
      this.validateCallbacks(props);
      this.callbacks = props;
    }
    this.log("constructor", "Initializing SchedulerBloc", {
      hasProps: !!props,
      appointmentType: props?.appointmentType
    });
    if (props?.appointmentType) {
      void this.fetchPreviousProvider(props.appointmentType);
    }
  }
  //#endregion

  //#region Getters/Setters
  get appointmentType(): ScheduleAppointmentTypes | undefined {
    return this.currentAppointmentType ?? this.rescheduleAppointment?.type;
  }
  get cacheKey() {
    return `${this.currentAppointmentType}-${this.rescheduleAppointment?.id}-${this.requestProviders?.join("-")}`;
  }
  get showSelectProvider() {
    return (
      this.state.selectedSlotGroup &&
      this.state.selectedSlotGroup.slots.length > 1
    );
  }
  //#endregion

  //#region Lifecycle/Props
  setProps = (props: SchedulerProps) => {
    this.log("setProps", "Setting props", {
      appointmentType: props.appointmentType
    });
    this.validateCallbacks(props);
    this.callbacks = props;
  };
  //#endregion

  //#region Logging & Validation
  log = (area: string, message: string, data?: Record<string, unknown>) => {
    if (this.enableSchedulerLogs) {
      // eslint-disable-next-line no-console
      console.log(`[Scheduler:${area}] ${message}`, data);
    }
  };

  private validateCallbacks = (props: Partial<SchedulerProps>) => {
    const callbackNames: Array<keyof SchedulerProps> = [
      "onSelectDate",
      "onSelectSlot",
      "onSelectSlotGroup",
      "onDeselectSlot",
      "onSelectProvider",
      "onAppointmentBooked",
      "onAppointmentRescheduled",
      "onAcceptReconnectProvider",
      "onRejectReconnectProvider",
      "onPreviousProviderDismissed"
    ];
    for (const name of callbackNames) {
      const callback = props[name];
      if (callback !== undefined && typeof callback !== "function") {
        this.log("validateCallbacks", `Warning: ${name} is not a function`, {
          type: typeof callback
        });
        // eslint-disable-next-line no-console
        console.warn(`Warning: ${name} is not a function`);
      }
    }
  };

  private callbackManager = (
    name: keyof SchedulerProps,
    ...args: unknown[]
  ) => {
    try {
      const callback = this.callbacks[name];
      if (typeof callback === "function") {
        if (this.debugCallbacks) {
          this.log("callbackManager", `Starting callback: ${name}`, { args });
        }
        (callback as (...callbackArgs: unknown[]) => void)(...args);
      }
    } catch (error: unknown) {
      this.log("callbackManager", `Error in callback ${name}`, {
        error: error instanceof Error ? error.message : String(error)
      });
      // eslint-disable-next-line no-console
      console.error(`Error in ${name} callback:`, error);
    }
  };
  //#endregion

  //#region Date/Slot Selection Methods
  setFocusedDate = (d: DateValue) => {
    this.log("setFocusedDate", "Setting focused date", { date: d });
    this.focusedDate = d;
    this.pickFocusedDate();
  };

  pickFocusedDate = () => {
    if (!this.focusedDate) {
      this.log("pickFocusedDate", "Error: No date focused");
      throw new Error("No date focused");
    }
    this.log("pickFocusedDate", "Picking focused date", {
      date: this.focusedDate
    });
    this.patch({
      selectedDate: this.focusedDate,
      selectedSlot: undefined,
      selectedSlotGroup: undefined
    });
    this.callbackManager("onSelectDate", this.focusedDate as CalendarDate);
  };

  clearScheduler = () => {
    this.currentMonth = today(getLocalTimeZone()).month;
    this.availableSlots = [];
    this.patch({
      selectedSlot: undefined,
      selectedSlotGroup: undefined,
      availableSlotGroups: [],
      selectedDate: undefined
    });
  };

  clearSelectedDate = () => {
    this.log("clearSelectedDate", "Clearing selected date");
    this.patch({
      selectedDate: undefined,
      selectedSlot: undefined,
      selectedSlotGroup: undefined
    });
  };

  clearSelectedSlot = () => {
    this.log("clearSelectedSlot", "Clearing selected slot");
    this.patch({
      selectedSlot: undefined
    });
  };
  //#endregion

  //#region Step/State Methods
  getActiveStep = (): SchedulerStep => {
    let step: SchedulerStep = "pick-date";
    if (this.state.selectedDate) {
      step = "pick-slot";
    }
    if (this.state.selectedSlotGroup) {
      step = "pick-provider";
    }
    if (this.state.selectedSlot) {
      step = "confirm";
    }
    this.log("getActiveStep", `Current step: ${step}`);
    return step;
  };
  
  getSlotsForDate = (date?: DateValue): AppointmentSlotGroup[] => {
    const selectedDate = date?.toDate(getLocalTimeZone()).toDateString();
    this.log("getSlotsForDate", "Getting slots for date", {
      selectedDateString: selectedDate,
      date
    });
    const slots = this.state.availableSlotGroups.filter(
      (s) => selectedDate === s.from.toDate().toDateString()
    );
    this.log("getSlotsForDate", "Found slots", {
      date: selectedDate,
      count: slots.length
    });
    return slots;
  };

  getSlotsForSelectedDate = (): AppointmentSlotGroup[] => {
    return this.getSlotsForDate(this.state.selectedDate);
  };

  datesAreEqual = (date1: DateValue, date2: DateValue): boolean => {
    const diff = date1.compare(date2);
    return diff === 0;
  };
  //#endregion

  //#region Slot/Provider Selection Handlers
  setSelectedSlot = (slot: AppointmentSlot | undefined) => {
    this.log(
      "setSelectedSlot",
      slot ? "Setting selected slot" : "Clearing selected slot",
      {
        slotFrom: slot?.from,
        slotTo: slot?.to,
        provider: slot?.participants[0]?.displayName
      }
    );
    const { selectedSlot } = this.state;
    let { selectedSlotGroup } = this.state;
    if (slot === undefined && selectedSlotGroup?.slots.length === 1) {
      selectedSlotGroup = undefined;
    }
    this.patch({ selectedSlot: slot, selectedSlotGroup });
    this.handleSelectSlot(slot, { slotRemoved: selectedSlot !== undefined });
  };

  setSelectedSlotGroup = (slotGroup: AppointmentSlotGroup | undefined) => {
    this.log(
      "setSelectedSlotGroup",
      slotGroup
        ? "Setting selected slot group"
        : "Clearing selected slot group",
      {
        from: slotGroup?.from,
        to: slotGroup?.to,
        slotsCount: slotGroup?.slots.length
      }
    );
    let selectedSlot = undefined;
    if (slotGroup && slotGroup.slots.length === 1) {
      selectedSlot = slotGroup.slots[0];
      this.log(
        "setSelectedSlotGroup",
        "Auto-selecting slot from group with only one slot",
        {
          provider: selectedSlot.participants[0]?.displayName
        }
      );
    }
    this.patch({ selectedSlotGroup: slotGroup, selectedSlot });
    this.handleSelectSlotGroup(slotGroup);
    if (selectedSlot) {
      this.handleSelectSlot(selectedSlot, { auto: true });
    }
  };

  selectProvider = (provider: { id: string; name: string }) => {
    this.log("selectProvider", "Provider selected", {
      id: provider.id,
      name: provider.name
    });
    this.callbackManager("onSelectProvider", provider);
  };

  handleSelectSlot = (
    slot: AppointmentSlot | undefined,
    options: { auto?: boolean; slotRemoved?: boolean } = {}
  ) => {
    this.log(
      "handleSelectSlot",
      slot ? "Handling slot selection" : "Handling slot deselection",
      {
        auto: options.auto,
        slotRemoved: options.slotRemoved,
        slotFrom: slot?.from,
        slotTo: slot?.to,
        provider: slot?.participants[0]?.displayName
      }
    );
    if (slot) {
      this.callbackManager("onSelectSlot", {
        autoSelected: options.auto,
        dateTime: toCalendarDateTime(slot.from),
        duration: this.calculateSlotDuration(slot),
        slotProviderId: slot.participants[0].userId,
        slotProviderName: slot.participants[0].displayName ?? ""
      });
    }
    if (!slot && options.slotRemoved) {
      this.callbackManager("onDeselectSlot");
    }
  };

  handleSelectSlotGroup = (slotGroup: AppointmentSlotGroup | undefined) => {
    this.log(
      "handleSelectSlotGroup",
      slotGroup ? "Slot group selected" : "Slot group deselected",
      {
        from: slotGroup?.from,
        to: slotGroup?.to,
        slotsCount: slotGroup?.slots.length
      }
    );
    if (slotGroup) {
      this.callbackManager("onSelectSlotGroup", {
        dateTime: toCalendarDateTime(slotGroup.from),
        duration: this.calculateSlotDuration(slotGroup.slots[0]),
        slotProviderIds: slotGroup.slots.flatMap((s) =>
          s.participants.map((p) => p.userId)
        ),
        slotProviderNames: slotGroup.slots.flatMap((s) =>
          s.participants.map((p) => p.displayName ?? "")
        )
      });
    }
  };
  //#endregion

  //#region Booking/Rescheduling Methods
  bookSelectedSlot = async (options: {
    rescheduleAppointment?: AppointmentResponse;
  }): Promise<AppointmentResponse> => {
    if (!this.currentAppointmentType) {
      this.log("bookSelectedSlot", "Error: No appointment type provided");
      throw new Error("No appointment type provided");
    }
    if (!this.state.selectedSlot) {
      this.log("bookSelectedSlot", "Error: No selected slot provided");
      throw new Error("No selected slot provided");
    }
    const { rescheduleAppointment } = options;
    const isReschedule = rescheduleAppointment !== undefined;
    const start = this.state.selectedSlot.from.toDate().toISOString();
    const end = this.state.selectedSlot.to.toDate().toISOString();
    const participantUserIds = this.state.selectedSlot.participants.map(
      (p) => p.userId
    );
    this.log(
      "bookSelectedSlot",
      `${isReschedule ? "Rescheduling" : "Booking"} appointment`,
      {
        appointmentType: this.currentAppointmentType,
        isReschedule,
        start,
        end,
        participantUserIds,
        rescheduleId: rescheduleAppointment?.id
      }
    );
    try {
      if (isReschedule) {
        const updatedAppointment =
          await AppointmentControllerService.updateMemberAppointment(
            rescheduleAppointment.id,
            {
              status: rescheduleAppointment.status,
              start,
              end,
              participantUserIds
            }
          );
        this.log("bookSelectedSlot", "Appointment rescheduled successfully", {
          appointmentId: updatedAppointment.data.id
        });
        this.handleAppointmentRescheduled(
          rescheduleAppointment,
          updatedAppointment.data
        );
        return updatedAppointment.data;
      } else {
        const newAppointment =
          await AppointmentSchedulingControllerService.scheduleAppointment(
            this.currentAppointmentType,
            {
              componentIdentifier: this.state.selectedSlot.componentIdentifier,
              start,
              end,
              participantUserIds
            }
          );
        this.log("bookSelectedSlot", "Appointment booked successfully", {
          appointmentId: newAppointment.data.id
        });
        this.handleAppointmentBooked(newAppointment.data);
        return newAppointment.data;
      }
    } catch (error) {
      this.log(
        "bookSelectedSlot",
        `Error ${isReschedule ? "rescheduling" : "booking"} appointment`,
        {
          error
        }
      );
      throw error;
    }
  };

  handleAppointmentBooked = (appointment: AppointmentResponse) => {
    if (!this.state.selectedSlot) {
      this.log("handleAppointmentBooked", "Error: No selected slot provided");
      throw new Error("No selected slot provided");
    }
    this.log("handleAppointmentBooked", "Handling appointment booked", {
      appointmentId: appointment.id,
      start: appointment.start
    });
    const appointmentEventData: SchedulerAppointmentEventData = {
      id: appointment.id,
      dateTime: toCalendarDateTime(
        fromDate(new Date(appointment.start), getLocalTimeZone())
      ),
      duration: this.calculateSlotDuration(this.state.selectedSlot)
    };
    this.callbackManager("onAppointmentBooked", appointmentEventData);
  };

  handleAppointmentRescheduled = (
    oldAppointment: AppointmentResponse,
    newAppointment: AppointmentResponse
  ) => {
    if (!this.state.selectedSlot) {
      this.log(
        "handleAppointmentRescheduled",
        "Error: No selected slot provided"
      );
      throw new Error("No selected slot provided");
    }
    this.log(
      "handleAppointmentRescheduled",
      "Handling appointment rescheduled",
      {
        oldAppointmentId: oldAppointment.id,
        oldStart: oldAppointment.start,
        newAppointmentId: newAppointment.id,
        newStart: newAppointment.start
      }
    );
    const duration = this.calculateSlotDuration(this.state.selectedSlot);
    this.callbackManager(
      "onAppointmentRescheduled",
      {
        id: oldAppointment.id,
        dateTime: toCalendarDateTime(
          fromDate(new Date(oldAppointment.start), getLocalTimeZone())
        ),
        duration
      },
      {
        id: newAppointment.id,
        dateTime: toCalendarDateTime(
          fromDate(new Date(newAppointment.start), getLocalTimeZone())
        ),
        duration
      }
    );
  };
  //#endregion

  //#region API/Async Methods
  initDatePickerView = async (options: {
    type: ScheduleAppointmentTypes;
    rescheduleAppointment?: AppointmentResponse;
    participantUserIds?: string[];
    requestProviders?: string[];
  }): Promise<void> => {
    if (this.state.loading) {
      this.log(
        "initDatePickerView",
        "Already loading, skipping initialization"
      );
      return;
    }
    this.log("initDatePickerView", "Initializing date picker view", {
      type: options.type,
      isReschedule: !!options.rescheduleAppointment,
      rescheduleId: options.rescheduleAppointment?.id,
      hasRequestProviders: !!options.requestProviders,
      requestProvidersCount: options.requestProviders?.length
    });
    this.currentAppointmentType = options.type;
    this.rescheduleAppointment = options.rescheduleAppointment;
    this.requestProviders = options.requestProviders;
    await this.loadAppointmentSlotsForCurrentMonth();
  };

  loadAppointmentSlotsForTimeRange = async (options: {
    type: ScheduleAppointmentTypes;
    start: CalendarDate;
    end: CalendarDate;
  }): Promise<void> => {
    const { type, start, end } = options;
    this.log(
      "loadAppointmentSlotsForTimeRange",
      "Loading appointment slots for time range",
      {
        type,
        start,
        end
      }
    );
    try {
      const startDate = toCalendarDateTime(start).set({
        hour: 0,
        minute: 0,
        second: 0,
        millisecond: 0
      });
      const endDate = toCalendarDateTime(end).set({
        hour: 0,
        minute: 0,
        second: 0,
        millisecond: 0
      });
      let providerList = this.rescheduleAppointment
        ? this.rescheduleAppointment.participants.map((p) => p.userId)
        : undefined;
      if (this.requestProviders) {
        providerList = this.requestProviders;
      }
      this.log(
        "loadAppointmentSlotsForTimeRange",
        "Fetching available slots from API",
        {
          type,
          startDate,
          endDate,
          providerList
        }
      );
      const availableSlots =
        await AppointmentSchedulingControllerService.getAvailableSlots(
          type,
          startDate.toDate(getLocalTimeZone()).toISOString(),
          endDate.toDate(getLocalTimeZone()).toISOString(),
          providerList
        );
      this.log("loadAppointmentSlotsForTimeRange", "Received slots from API", {
        count: availableSlots.data.length
      });
      const slots = this.parseSlotsFromApiResponse(availableSlots.data);
      const mergedSlots = this.mergeSlots(slots);
      this.log("loadAppointmentSlotsForTimeRange", "Updated available slots", {
        parsedCount: slots.length,
        mergedCount: mergedSlots.length,
        totalInState: this.state.availableSlotGroups.length + mergedSlots.length
      });
      this.patch({
        availableSlotGroups: mergedSlots
      });
    } catch (e) {
      this.log(
        "loadAppointmentSlotsForTimeRange",
        "Error loading appointment slots",
        {
          error: e
        }
      );
      // eslint-disable-next-line no-console
      console.error(e);
    }
  };

  loadAppointmentSlotsForCurrentMonth = async (): Promise<void> => {
    this.log(
      "loadAppointmentSlotsForCurrentMonth",
      "Loading slots for current month",
      {
        month: this.currentMonth,
        cacheKey: this.cacheKey,
        currentCacheKey: this.currentCacheKey,
        loadedMonths: Array.from(this.loadCompleteForMonths)
      }
    );
    this.clearSelectedDate();
    if (this.currentCacheKey !== this.cacheKey) {
      this.log(
        "loadAppointmentSlotsForCurrentMonth",
        "Cache key changed, resetting state"
      );
      this.patch({
        availableSlotGroups: [],
        selectedSlot: undefined,
        selectedSlotGroup: undefined,
        selectedDate: undefined
      });
      this.loadCompleteForMonths.clear();
      this.currentCacheKey = this.cacheKey;
    }
    if (this.loadCompleteForMonths.has(this.currentMonth)) {
      this.log(
        "loadAppointmentSlotsForCurrentMonth",
        "Month already loaded, skipping",
        {
          month: this.currentMonth
        }
      );
      return;
    }
    this.log("loadAppointmentSlotsForCurrentMonth", "Starting to load month", {
      month: this.currentMonth
    });
    this.patch({
      loading: true
    });
    const requestListForMonth: Parameters<
      typeof this.loadAppointmentSlotsForTimeRange
    >[0][] = [];
    const { currentMonth } = this;
    const dateNow = today(getLocalTimeZone());
    if (!this.currentAppointmentType) {
      this.log(
        "loadAppointmentSlotsForCurrentMonth",
        "Error: No appointment type provided"
      );
      throw new Error("No appointment type provided");
    }
    let start =
      currentMonth === dateNow.month
        ? dateNow
        : startOfMonth(dateNow).set({ month: currentMonth, day: 1 });
    let ended = false;
    let limit = 10;
    while (!ended && limit > 0) {
      limit--;
      const startCalendar = toCalendarDate(start);
      let end = startCalendar.add({ days: this.daysPerRequest });
      if (end.month !== currentMonth) {
        end = endOfMonth(start).add({ days: 1 });
        ended = true;
      }
      if (end.compare(start) < 0) {
        break;
      }
      requestListForMonth.push({
        start,
        end,
        type: this.currentAppointmentType
      });
      start = end;
    }
    this.log(
      "loadAppointmentSlotsForCurrentMonth",
      "Prepared requests for month",
      {
        requestCount: requestListForMonth.length,
        daysPerRequest: this.daysPerRequest
      }
    );
    await Promise.all(
      requestListForMonth.map((request) =>
        this.loadAppointmentSlotsForTimeRange(request)
      )
    );
    this.log("loadAppointmentSlotsForCurrentMonth", "Finished loading month", {
      month: this.currentMonth,
      totalSlotsLoaded: this.state.availableSlotGroups.length
    });
    this.patch({
      loading: false
    });
    this.loadCompleteForMonths.add(currentMonth);
  };

  handleDateFocusChanged = (focusedDate: DateValue) => {
    this.log("handleDateFocusChanged", "Date focus changed", {
      date: focusedDate
    });
    clearTimeout(this.debouncedHandleDateFocusChanged);
    this.debouncedHandleDateFocusChanged = setTimeout(() => {
      const newFocusedMonth = focusedDate.month;
      if (this.currentMonth !== newFocusedMonth) {
        this.log("handleDateFocusChanged", "Month changed, loading new month", {
          oldMonth: this.currentMonth,
          newMonth: newFocusedMonth
        });
        this.currentMonth = newFocusedMonth;
        void this.loadAppointmentSlotsForCurrentMonth();
      }
    }, 300);
  };

  fetchPreviousProvider = async (appointmentType: ScheduleAppointmentTypes) => {
    this.log("fetchPreviousProvider", "Fetching previous provider", {
      appointmentType
    });
    this.patch({ previousProviderLoading: true });
    try {
      const { data } =
        await AppointmentControllerService.getParticipantsInfoForMostRecentAppointmentWithType(
          appointmentType
        );
      const previousProvider = (data[0] ??
        null) as AppointmentParticipantResponse | null;
      this.log("fetchPreviousProvider", "Previous provider fetched", {
        providerFound: !!previousProvider,
        providerName: previousProvider?.displayName,
        providerId: previousProvider?.userId
      });
      this.patch({
        previousProvider,
        previousProviderLoading: false,
        reconnectStatus: data.length > 0 ? "available" : "unavailable"
      });
    } catch (error) {
      this.log("fetchPreviousProvider", "Error fetching previous provider", {
        error
      });
      // eslint-disable-next-line no-console
      console.error("Error fetching previous provider:", error);
      this.patch({ previousProvider: null, previousProviderLoading: false });
    }
  };
  //#endregion

  //#region Preference/Provider Reconnect
  handlePreferenceChangeReconnectProvider = (
    action: SchedulerReconnectStatus
  ) => {
    this.log(
      "handlePreferenceChangeReconnectProvider",
      "Provider reconnect preference changed",
      {
        action,
        previousProvider: this.state.previousProvider?.displayName,
        previousProviderId: this.state.previousProvider?.userId
      }
    );
    this.patch({
      reconnectStatus: action
    });
    switch (action) {
      case "accepted":
        this.callbackManager("onAcceptReconnectProvider", action);
        break;
      case "rejected":
        this.callbackManager("onRejectReconnectProvider", action);
        break;
      case "dismissed":
        this.clearScheduler();
        this.callbackManager("onPreviousProviderDismissed", action);
        break;
    }
  };
  //#endregion

  //#region Helpers/Utilities
  static dateToString = (date: DateValue) => {
    return date.toString().split("T")[0];
  };

  calculateSlotDuration = (slot: AppointmentSlot): number => {
    const from = slot.from.toDate();
    const to = slot.to.toDate();
    return Math.floor((to.getTime() - from.getTime()) / 60000);
  };

  mergeSlots = (slots: AppointmentSlotGroup[]): AppointmentSlotGroup[] => {
    const mergedSlots: AppointmentSlotGroup[] = [
      ...this.state.availableSlotGroups,
      ...slots
    ];
    const slotMap = new Map<string, AppointmentSlotGroup>();
    mergedSlots.forEach((slot) => {
      const key = `${slot.from.toString()}-${slot.to.toString()}`;
      if (!slotMap.has(key)) {
        slotMap.set(key, slot);
      }
    });
    const uniqueSlots = [...slotMap.values()];
    this.log("mergeSlots", "Merged and deduplicated slots", {
      incomingCount: slots.length,
      existingCount: this.state.availableSlotGroups.length,
      combinedCount: mergedSlots.length,
      uniqueCount: uniqueSlots.length
    });
    return uniqueSlots;
  };
  
  parseSlotsFromApiResponse = (
    slots: Array<AvailableSlotResponse>
  ): AppointmentSlotGroup[] => {
    this.log("parseSlotsFromApiResponse", "Parsing slots from API response", {
      slotCount: slots.length
    });
    const validSlots = slots;
    const availableSlots = validSlots.map((slot) => {
      const from = fromDate(new Date(slot.start), getLocalTimeZone());
      const to = fromDate(new Date(slot.end), getLocalTimeZone());
      return {
        from,
        to,
        participants: slot.participants,
        componentIdentifier: slot.componentIdentifier
      } satisfies AppointmentSlot;
    });
    this.availableSlots = availableSlots;
    let groupedSlots: AppointmentSlotGroup[] = [];
    availableSlots.forEach((slot) => {
      const { from } = slot;
      const { to } = slot;
      const slots = groupedSlots.find(
        (s) => this.datesAreEqual(s.from, from) && this.datesAreEqual(s.to, to)
      );
      if (slots) {
        slots.slots.push(slot);
      } else {
        groupedSlots.push({
          from,
          to,
          slots: [slot]
        });
      }
    });
    groupedSlots = groupedSlots.map((sg) => {
      const sortedSlots = sg.slots.sort((a, b) => {
        const aName = a.participants[0].displayName?.toLowerCase() ?? "";
        const bName = b.participants[0].displayName?.toLowerCase() ?? "";
        if (aName < bName) return -1;
        if (aName > bName) return 1;
        return 0;
      });
      return {
        ...sg,
        slots: sortedSlots
      };
    });
    this.log("parseSlotsFromApiResponse", "Parsed and grouped slots", {
      availableSlotCount: availableSlots.length,
      groupedSlotCount: groupedSlots.length
    });
    return groupedSlots;
  };
  //#endregion
}
